/**
 * 用户认证状态管理 - 全局 Context 版本
 * 零侵入：独立于现有系统，支持双系统并行
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { userApi, checkMongoDBHealth } from '../services/mongoUserApi';

// 存储键名
const STORAGE_KEYS = {
  MONGO_USER: 'mongo_user',
  MONGO_TOKEN: 'mongo_token',
  AUTH_SYSTEM: 'auth_system', // 'json' | 'mongodb'
};

// 安全读取 localStorage
const safeLocalStorageGet = (key, defaultValue = null) => {
  try {
    const value = localStorage.getItem(key);
    if (value && (key === STORAGE_KEYS.MONGO_USER || key === 'user')) {
      return JSON.parse(value);
    }
    return value || defaultValue;
  } catch (e) {
    console.error('Failed to read localStorage:', key, e);
    return defaultValue;
  }
};

// 安全写入 localStorage
const safeLocalStorageSet = (key, value) => {
  try {
    if (typeof value === 'object') {
      localStorage.setItem(key, JSON.stringify(value));
    } else {
      localStorage.setItem(key, value);
    }
    return true;
  } catch (e) {
    console.error('Failed to write localStorage:', key, e);
    return false;
  }
};

// 创建 Context
const AuthContext = createContext(null);

// Provider 组件
export const AuthProvider = ({ children }) => {
  // 当前使用的认证系统
  const [authSystem, setAuthSystem] = useState(() => {
    return safeLocalStorageGet(STORAGE_KEYS.AUTH_SYSTEM, 'json');
  });

  // MongoDB 用户状态 - 始终从 localStorage 读取作为单一真实来源
  const [mongoUser, setMongoUser] = useState(() => {
    return safeLocalStorageGet(STORAGE_KEYS.MONGO_USER, null);
  });

  // MongoDB 连接状态
  const [mongoAvailable, setMongoAvailable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 检查 MongoDB 健康状态
  const checkMongoHealth = useCallback(async () => {
    try {
      await checkMongoDBHealth();
      setMongoAvailable(true);
      return true;
    } catch (err) {
      setMongoAvailable(false);
      return false;
    }
  }, []);

  // MongoDB 用户登录
  const mongoLogin = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);

    try {
      // 先检查 MongoDB 可用性
      const available = await checkMongoHealth();
      if (!available) {
        throw new Error('MongoDB 服务暂时不可用，请使用旧系统登录');
      }

      const response = await userApi.login({ email, password });

      if (response.success && response.data) {
        const userData = response.data;
        // 先写入 localStorage，这是单一真实来源
        safeLocalStorageSet(STORAGE_KEYS.MONGO_USER, userData);
        safeLocalStorageSet(STORAGE_KEYS.AUTH_SYSTEM, 'mongodb');
        // 再更新 React 状态
        setMongoUser(userData);
        setAuthSystem('mongodb');
        setLoading(false);
        return { success: true, user: userData };
      }

      throw new Error(response.message || '登录失败');
    } catch (err) {
      const errorMsg = err.message || '网络错误，请稍后重试';
      setError(errorMsg);
      setLoading(false);
      return { success: false, error: errorMsg };
    }
  }, [checkMongoHealth]);

  // MongoDB 用户注册
  const mongoRegister = useCallback(async (username, email, password) => {
    setLoading(true);
    setError(null);

    try {
      const available = await checkMongoHealth();
      if (!available) {
        throw new Error('MongoDB 服务暂时不可用，请使用旧系统注册');
      }

      const response = await userApi.register({ username, email, password });

      if (response.success) {
        setLoading(false);
        return {
          success: true,
          message: response.message || '注册成功！',
          user: response.data
        };
      }

      throw new Error(response.message || '注册失败');
    } catch (err) {
      const errorMsg = err.message || '网络错误，请稍后重试';
      setError(errorMsg);
      setLoading(false);
      return { success: false, error: errorMsg };
    }
  }, [checkMongoHealth]);

  // 登出
  const logout = useCallback(() => {
    setMongoUser(null);
    localStorage.removeItem(STORAGE_KEYS.MONGO_USER);
    localStorage.removeItem(STORAGE_KEYS.MONGO_TOKEN);
    // 不清除 auth_system，保留用户偏好
  }, []);

  // 切换认证系统
  const switchAuthSystem = useCallback((system) => {
    if (system === 'mongodb' || system === 'json') {
      setAuthSystem(system);
      safeLocalStorageSet(STORAGE_KEYS.AUTH_SYSTEM, system);
      // 切换时清除对方系统的登录状态
      if (system === 'mongodb') {
        localStorage.removeItem('user'); // JSON 系统的用户
      } else {
        logout();
      }
    }
  }, [logout]);

  // 初始化检查 MongoDB 健康状态
  useEffect(() => {
    checkMongoHealth();
  }, [checkMongoHealth]);

  // 从 localStorage 同步状态（处理跨标签页或外部修改）
  useEffect(() => {
    const syncFromStorage = () => {
      const storedUser = safeLocalStorageGet(STORAGE_KEYS.MONGO_USER, null);
      const storedSystem = safeLocalStorageGet(STORAGE_KEYS.AUTH_SYSTEM, 'json');

      // 如果 localStorage 有变化，同步到 React 状态
      if (JSON.stringify(storedUser) !== JSON.stringify(mongoUser)) {
        setMongoUser(storedUser);
      }
      if (storedSystem !== authSystem) {
        setAuthSystem(storedSystem);
      }
    };

    // 监听 storage 事件（跨标签页同步）
    window.addEventListener('storage', syncFromStorage);

    // 每 500ms 检查一次（处理同标签页的外部修改）
    const interval = setInterval(syncFromStorage, 500);

    return () => {
      window.removeEventListener('storage', syncFromStorage);
      clearInterval(interval);
    };
  }, [mongoUser, authSystem]);

  const contextValue = {
    // 状态
    authSystem,
    mongoUser,
    mongoAvailable,
    loading,
    error,

    // 认证方法
    mongoLogin,
    mongoRegister,
    logout,

    // 工具方法
    checkMongoHealth,
    switchAuthSystem,

    // 快捷判断 - 基于 React state，确保依赖检测生效
    isLoggedInMongo: !!mongoUser,
    isLoggedInJson: !!safeLocalStorageGet('user', null),
    isLoggedIn: !!mongoUser || !!safeLocalStorageGet('user', null),

    // 更新本地用户状态（用于资料更新后刷新）
    updateUserState: (userData) => {
      // 更新 React state
      setMongoUser(userData);
      // 更新 localStorage
      safeLocalStorageSet(STORAGE_KEYS.MONGO_USER, userData);
    },
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// 自定义 Hook - 使用 Context
export function useAuthStore() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthStore must be used within an AuthProvider');
  }
  return context;
}

/**
 * 独立的认证工具函数（非 Hook 版）
 */
export const authUtils = {
  getCurrentUser: () => {
    const system = safeLocalStorageGet(STORAGE_KEYS.AUTH_SYSTEM, 'json');
    if (system === 'mongodb') {
      return safeLocalStorageGet(STORAGE_KEYS.MONGO_USER, null);
    } else {
      return safeLocalStorageGet('user', null);
    }
  },

  getAuthSystem: () => {
    return safeLocalStorageGet(STORAGE_KEYS.AUTH_SYSTEM, 'json');
  },

  isLoggedIn: () => {
    return !!safeLocalStorageGet(STORAGE_KEYS.MONGO_USER, null) || !!safeLocalStorageGet('user', null);
  },

  logout: () => {
    localStorage.removeItem(STORAGE_KEYS.MONGO_USER);
    localStorage.removeItem(STORAGE_KEYS.MONGO_TOKEN);
    localStorage.removeItem('user');
  },
};
